import React, { useEffect, useMemo } from "react";
import dayjs, { Dayjs } from "dayjs";
import isSameOrBefore from "dayjs/plugin/isSameOrBefore";
import isSameOrAfter from "dayjs/plugin/isSameOrAfter";

import { formatPlainTime } from "../../../core/utils/configFormatters";
import { useAppConfig } from "../../../customhooks/useAppConfig";
import { useDispatch, useSelector } from "react-redux";
import { fetchAllWeekoff } from "../../../core/data/redux/weekoff/weekoff.Action";
import { AppDispatch } from "../../../core/data/redux/store";


dayjs.extend(isSameOrBefore);
dayjs.extend(isSameOrAfter);

/* ---------- Types ---------- */
type EmployeeCal = {
  EmployeeID: number;
  Name: string;
  PictureURL?: string;
  EmployeeNumber?: string;
  DepartmentId?: number;
  BranchId?: number | string;
  branch?: number | string;
  BranchID?: number | string;
};

type WeekPattern = Record<
  string, // "Sunday" | "Monday" | ...
  { all: boolean; weeks: Record<1 | 2 | 3 | 4 | 5, boolean> }
>;

type WeekoffItem = {
  Status?: string;                // "active"
  Type?: "employee" | "department" | "branch";
  AppliedTo?: string | number[];  // ids (stringified or array)
  WeekPattern?: WeekPattern | string;
};

type ShiftCalendarProps = {
  employees: EmployeeCal[];
  shiftSchedules: any[];
  startDate: Dayjs;
  endDate: Dayjs;
  holidays: any[];        // same structure you showed
  weekoff?: WeekoffItem[]; // NEW: pass weekoff list here
  onCellClick?: (args: {
    employee: EmployeeCal;
    date: Dayjs;
    schedule: any | null;
  }) => void;
};

/* ---------- Helpers ---------- */
const WEEK_OFF_VALUE = -1 as const;

const normalizeWeekPattern = (raw: any): WeekPattern => {
  if (!raw) return {};
  if (typeof raw === "string") {
    try { return JSON.parse(raw); } catch { return {}; }
  }
  return raw;
};

const getWeekIndexInMonth = (date: Dayjs): 1 | 2 | 3 | 4 | 5 => {
  const idx = Math.floor((date.date() - 1) / 7) + 1;
  return idx as 1 | 2 | 3 | 4 | 5;
};

const toIdArray = (appliedTo: any): number[] => {
  if (appliedTo == null) return [];
  try {
    const parsed = typeof appliedTo === "string" ? JSON.parse(appliedTo) : appliedTo;
    if (Array.isArray(parsed)) return parsed.map((x) => Number(x));
  } catch { }
  const n = Number(appliedTo);
  return Number.isFinite(n) ? [n] : [];
};

/* ---------- Component ---------- */
const ShiftCalendar: React.FC<ShiftCalendarProps> = ({
  employees,
  shiftSchedules,
  startDate,
  endDate,
  onCellClick,
  holidays,
}) => {
  const config = useAppConfig();
  const dispatch = useDispatch<AppDispatch>();

  // Generate day range
  const days: Dayjs[] = useMemo(() => {
    const arr: Dayjs[] = [];
    let cur = startDate.startOf("day");
    while (cur.isBefore(endDate) || cur.isSame(endDate, "day")) {
      arr.push(cur);
      cur = cur.add(1, "day");
    }
    return arr;
  }, [startDate, endDate]);

  const { weekoff } = useSelector((state: any) => state.weekoff);

  // 🔒 Fixed holidays map: YYYY-MM-DD -> { Name, Id }
  const fixedHolidayByDate = useMemo(() => {
    const map = new Map<string, { Name: string; Id: number }>();

    (holidays ?? []).forEach((h: any) => {
      const isActive = String(h?.Status ?? "").toLowerCase() === "active";
      const isFixed = String(h?.HolidayType ?? "").toLowerCase() === "fixed";

      if (!isActive || !isFixed) return;
      if (!h?.StartDate || !h?.EndDate) return;

      let start = dayjs(h.StartDate);
      let end = dayjs(h.EndDate);

      while (start.isSameOrBefore(end, "day")) {
        const d = start.format("YYYY-MM-DD");
        map.set(d, { Name: h?.Name ?? "Holiday", Id: Number(h?.Id) || 0 });

        start = start.add(1, "day");
      }
    });

    return map;
  }, [holidays]);

  useEffect(() => {
    dispatch(fetchAllWeekoff());
  }, [dispatch]);

  // 🗓️ Build weekly-off map with priority Employee → Department → Branch
  // result: Record<EmployeeID, Map<DayName, Set<WeekIndex>>>
  const employeeWeeklyOffMap = useMemo(() => {
    const empIndex = new Map<number, Map<string, Set<number>>>();
    const deptIndex = new Map<number, Map<string, Set<number>>>();
    const branchIndex = new Map<number, Map<string, Set<number>>>();


    const pushWeeks = (
      idx: Map<number, Map<string, Set<number>>>,
      id: number,
      wp: WeekPattern
    ) => {
      const current = idx.get(id) ?? new Map<string, Set<number>>();
      Object.entries(wp).forEach(([day, row]) => {
        const set = current.get(day) ?? new Set<number>();
        if (row?.all) {
          [1, 2, 3, 4, 5].forEach((w) => set.add(w));
        } else if (row?.weeks) {
          (Object.entries(row.weeks) as [any, boolean][])?.forEach(([wk, val]) => {
            if (val) set.add(Number(wk));
          });
        }
        if (set.size) current.set(day, set);
      });
      idx.set(id, current);
    };

    (weekoff ?? []).forEach((item: any) => {
      if (String(item?.Status ?? "").toLowerCase() !== "active") return;
      const ids = toIdArray(item?.AppliedTo);
      const wp = normalizeWeekPattern(item?.WeekPattern);
      if (!ids.length) return;

      const type = String(item?.Type || "").toLowerCase();
      if (type === "employee") ids.forEach((id) => pushWeeks(empIndex, id, wp));
      else if (type === "department") ids.forEach((id) => pushWeeks(deptIndex, id, wp));
      else if (type === "branch") ids.forEach((id) => pushWeeks(branchIndex, id, wp));
    });

    const result: Record<number, Map<string, Set<number>>> = {};
    (employees ?? []).forEach((emp) => {
      const empId = Number(emp?.EmployeeID);
      if (!Number.isFinite(empId)) return;

      const empWeeks = empIndex.get(empId);
      if (empWeeks) { result[empId] = empWeeks; return; }

      const deptId = Number(emp?.DepartmentId);
      const deptWeeks = Number.isFinite(deptId) ? deptIndex.get(deptId) : undefined;
      if (deptWeeks) { result[empId] = deptWeeks; return; }

      const brId = Number(emp?.branch ?? emp?.BranchId ?? emp?.BranchID);
      const brWeeks = Number.isFinite(brId) ? branchIndex.get(brId) : undefined;
      if (brWeeks) { result[empId] = brWeeks; return; }
    });

    return result;
  }, [weekoff, employees]);

  console.log("employeeWeeklyOffMap", employeeWeeklyOffMap)
  console.log("weekoff", weekoff)

  // Find schedule covering emp/date
  const findScheduleFor = (empId: number, date: Dayjs) => {
    return (
      shiftSchedules.find(
        (s: any) =>
          Array.isArray(s?.EmployeeIDs) &&
          s.EmployeeIDs.includes(empId) &&
          dayjs(s.FromDate).isSameOrBefore(date, "day") &&
          dayjs(s.ToDate).isSameOrAfter(date, "day")
      ) || null
    );
  };

  const renderCell = (emp: EmployeeCal, d: Dayjs) => {
    const dateStr = d.format("YYYY-MM-DD");
    const schedule = findScheduleFor(emp.EmployeeID, d);

    // 1) Fixed Holiday (highest priority)
    const fixedHoliday = fixedHolidayByDate.get(dateStr);
    if (fixedHoliday) {
      return (
        <td
          key={dateStr}
          className="cal-cell holiday"
          title={`${fixedHoliday.Name} — Fixed Holiday`}
          style={{
            background: "#fff1f0",
            border: "1px solid #ffccc7",
            color: "#cf1322",
            textAlign: "center",
            fontWeight: 600,
            cursor: "not-allowed",
          }}
        >
          Holiday {fixedHoliday.Name}
        </td>
      );
    }

    // 2) Weekly Off (show tag if no shift assigned)
    const dayLabel = d.format("dddd"); // e.g., "Monday"
    const weekIndex = getWeekIndexInMonth(d);
    const mapForEmp = employeeWeeklyOffMap[emp.EmployeeID]; // Map<DayName, Set<WeekIndex>>
    const isWeeklyOffByPattern = mapForEmp?.get(dayLabel)?.has(weekIndex) ?? false;

    if (!schedule && isWeeklyOffByPattern) {
      return (
        <td
          key={dateStr}
          className="cal-cell weekly-off"
          title="Weekly Off"
          style={{
            border: "1px dashed #d9d9d9",
            color: "#595959",
            textAlign: "center",
            fontWeight: 600,
            cursor: "default",
          }}
        >
          Weekly Off
        </td>
      );
    }

    // 3) Normal schedule (or empty)
    return (
      <td
        key={dateStr}
        onClick={() => {
          if (!schedule) return onCellClick?.({ employee: emp, date: d, schedule });
          if (schedule.ShiftName === "Weekly Off") return; // 🔒 block edit
          onCellClick?.({ employee: emp, date: d, schedule });
        }}
        style={{ cursor: "pointer", textAlign: "center", padding: 6 }}
        title={schedule ? "Click to edit schedule" : "Click to create schedule"}
      >
        {schedule ? (
          <div>
            <div style={{ fontWeight: 600 }}>
              {schedule.ShiftName || "Shift"}
            </div>

            {/* 👉 Hide timings for Weekly Off */}
            {schedule.ShiftName !== "Weekly Off" && (
              <div style={{ fontSize: 12 }}>
                ({formatPlainTime(schedule.StartTime, config)} –{" "}
                {formatPlainTime(schedule.EndTime, config)})
              </div>
            )}

            {/* If Weekly Off pattern applies */}
            {isWeeklyOffByPattern && schedule.ShiftName !== "Weekly Off" && (
              <div style={{ marginTop: 4, fontSize: 11, opacity: 0.75 }}>
                • Pattern: Weekly Off
              </div>
            )}
          </div>
        ) : (
          "—"
        )}
      </td>
    );

  };

  return (
    <div className="shift-calendar">
      <table style={{ width: "100%", borderCollapse: "collapse" }}>
        <thead>
          <tr>
            <th style={{ position: "sticky", left: 0, background: "#fff", zIndex: 2, textAlign: "left" }}>Employee</th>
            {days.map((d) => (
              <th key={d.format("YYYY-MM-DD")} style={{ textAlign: "center" }}>
                {d.format("DD-MMM")} <br /> {d.format("ddd")}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {employees?.map((emp) => (
            <tr key={emp.EmployeeID}>
              {/* Employee cell */}
              <td
                className="emp-cell"
                style={{
                  position: "sticky",
                  left: 0,
                  background: "#fff",
                  zIndex: 1,
                  display: "flex",
                  alignItems: "center",
                  gap: 8,
                  padding: 6,
                  borderRight: "1px solid #f0f0f0",
                }}
              >
                <img
                  src={
                    emp.PictureURL ||
                    "https://cdn-icons-png.flaticon.com/512/149/149071.png"
                  }
                  alt={emp.Name}
                  className="emp-photo"
                  width={28}
                  height={28}
                  style={{ borderRadius: "50%", objectFit: "cover" }}
                />
                <div className="emp-info" style={{ lineHeight: 1.2 }}>
                  <div style={{ fontWeight: 600 }}>{emp.Name}</div>
                  <div style={{ fontSize: 12, color: "#8c8c8c" }}>{emp?.EmployeeNumber}</div>
                </div>
              </td>

              {/* Day cells */}
              {days.map((d) => renderCell(emp, d))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

export default ShiftCalendar;
